import { TableCell, TableRow, Skeleton, cn } from '@parrot-software/design-system';
import type { ColumnDefinition } from '@reports/domain/value-objects/blocks/ColumnConfig';

/**
 * Display column with key and definition
 */
interface DisplayColumn {
  key: string;
  definition: ColumnDefinition;
}

interface TableBlockPaginationSkeletonProps {
  displayColumns: DisplayColumn[];
  rows?: number;
}

/**
 * TableBlockPaginationSkeleton - Loading skeleton rows for pagination
 *
 * Displays skeleton loading rows that appear while fetching the next page
 * in infinite scroll pagination. Renders only the table body rows, not the
 * full table structure.
 *
 * @param {TableBlockPaginationSkeletonProps} props - Component properties
 * @param {DisplayColumn[]} props.displayColumns - Array of visible columns to render
 * @param {number} [props.rows=3] - Number of skeleton rows to display
 * @returns {React.ReactElement} Fragment containing skeleton table rows
 *
 * @example
 * <TableBlockPaginationSkeleton displayColumns={displayColumns} rows={3} />
 */
export const TableBlockPaginationSkeleton = ({
  displayColumns,
  rows = 5,
}: TableBlockPaginationSkeletonProps): React.ReactElement => {
  return (
    <>
      {Array.from({ length: rows }).map((_, skeletonIndex) => (
        <TableRow className="border-b border-(--base-border-border)" key={`skeleton-row-${skeletonIndex}`}>
          {displayColumns.map(({ key, definition }) => (
            <TableCell className="px-4 py-2.5" key={`skeleton-cell-${skeletonIndex}-${key}`}>
              <div
                className={cn('flex', {
                  'justify-start': definition.align === 'left',
                  'justify-center': definition.align === 'center',
                  'justify-end': definition.align === 'right',
                })}
              >
                <Skeleton className="h-4 w-[70%] bg-(--base-accent-bg-accent50)" />
              </div>
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
};
